import React, { useEffect, useState } from 'react';
import {
  Drawer,
  Form,
  Select,
  DatePicker,
  InputNumber,
  Input,
  Button,
  Space,
  Divider,
  message,
  Typography,
  Tooltip,
  Table,
  Modal
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  ImportOutlined,
  EditOutlined,
  QuestionCircleOutlined,
  SaveOutlined
} from '@ant-design/icons';
import { useMutation, useQueryClient } from 'react-query';
import dayjs from 'dayjs';
import api from '../../services/api';

const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;

interface StockInModalProps {
  visible: boolean;
  onCancel: () => void;
  editingRecord?: any;
  medicines: any[];
  suppliers: any[];
  isLoadingDetail?: boolean;
}

interface StockInItem {
  medicine_id: number;
  quantity: number;
  unit_price: number;
  lot_number: string;
  expiry_date: any;
  manufacturing_date: any;
  notes?: string;
}

const StockInModal: React.FC<StockInModalProps> = ({
  visible,
  onCancel,
  editingRecord,
  medicines,
  suppliers,
  isLoadingDetail = false
}) => {
  const [form] = Form.useForm();
  const [forceUpdate, setForceUpdate] = useState(0);
  const [initialValues, setInitialValues] = useState<any>(null);
  const [isFormInitialized, setIsFormInitialized] = useState(false);
  const [items, setItems] = useState<StockInItem[]>([]);
  const [itemForm] = Form.useForm();
  const queryClient = useQueryClient();
  const [initialFormValues, setInitialFormValues] = useState<any>(null);
  const [initialItems, setInitialItems] = useState<StockInItem[]>([]);

  // Mutations
  const createMutation = useMutation(
    (data: any) => api.post('/api/stock/in', data),
    {
      onSuccess: () => {
        message.success('Tạo phiếu nhập kho thành công!');
        queryClient.invalidateQueries('stock-in');
        handleCancelInternal();
      },
      onError: (error: any) => {
        message.error(error.response?.data?.message || 'Có lỗi xảy ra khi tạo phiếu nhập kho');
      },
    }
  );

  const updateMutation = useMutation(
    ({ id, data }: { id: number; data: any }) => api.put(`/api/stock/in/${id}`, data),
    {
      onSuccess: () => {
        message.success('Cập nhật phiếu nhập kho thành công!');
        queryClient.invalidateQueries('stock-in');
        handleCancelInternal();
      },
      onError: (error: any) => {
        message.error(error.response?.data?.message || 'Có lỗi xảy ra khi cập nhật phiếu nhập kho');
      },
    }
  );

  // Reset form when drawer opens/closes
  useEffect(() => {
    if (visible) {
      if (editingRecord && !isLoadingDetail) {
        // Edit mode - populate form with existing data
        console.log('=== DRAWER OPEN - EDITING RECORD ===');
        console.log('editingRecord:', editingRecord);
        
        // API trả về { voucher: {...}, items: [...] }
        let voucherData, itemsData;
        
        if (editingRecord.voucher && editingRecord.items) {
          // Cấu trúc: { voucher: {...}, items: [...] }
          voucherData = editingRecord.voucher;
          itemsData = editingRecord.items;
        } else {
          // Fallback - nếu không có cấu trúc đúng thì không set dữ liệu
          console.log('=== INVALID DATA STRUCTURE ===');
          setInitialValues({ items: [] });
          setItems([]);
          setIsFormInitialized(true);
          return;
        }
        
        console.log('=== DATA EXTRACTION ===');
        console.log('voucherData:', voucherData);
        console.log('itemsData:', itemsData);
        console.log('itemsData.length:', itemsData.length);
        
        // Xử lý dữ liệu items
        const processedItems = itemsData.map((item: any, index: number) => {
          console.log(`=== PROCESSING ITEM ${index} ===`);
          console.log('Raw item:', item);
          
          const processedItem = {
            medicine_id: item.medicine_id,
            quantity: item.quantity || 0,
            unit_price: item.unit_price || 0,
            lot_number: item.lot_number || '',
            expiry_date: item.expiry_date ? dayjs(item.expiry_date) : null,
            manufacturing_date: item.manufacturing_date ? dayjs(item.manufacturing_date) : null,
            notes: item.notes || ''
          };
          
          console.log('Processed item:', processedItem);
          return processedItem;
        });
        
        console.log('=== FINAL PROCESSED ITEMS ===');
        console.log('processedItems:', processedItems);
        console.log('processedItems.length:', processedItems.length);
        
        const formData = {
          supplier_id: voucherData.supplier_id,
          notes: voucherData.notes || ''
        };
        
        console.log('=== FINAL FORM DATA ===');
        console.log('formData:', formData);
        
        setInitialValues(formData);
        setItems(processedItems);
        setIsFormInitialized(true);
        
      } else if (!editingRecord && !isLoadingDetail) {
        // Create mode - reset form
        console.log('=== DRAWER OPEN - CREATE MODE ===');
        setInitialValues({ items: [] });
        setItems([]);
        setIsFormInitialized(true);
      }
      // Nếu isLoadingDetail = true, không làm gì cả, chờ dữ liệu load xong
    } else {
      // Drawer closed - reset form
      console.log('=== DRAWER CLOSED ===');
      setInitialValues(null);
      setItems([]);
      setForceUpdate(0);
      setIsFormInitialized(false);
    }
  }, [visible, editingRecord, isLoadingDetail]);

  // Đảm bảo form được set values sau khi initialValues được set
  useEffect(() => {
    if (initialValues && isFormInitialized && visible) {
      console.log('=== SETTING FORM VALUES ===');
      console.log('initialValues:', initialValues);
      
      // Set form values với delay để đảm bảo form đã sẵn sàng
      setTimeout(() => {
        console.log('=== EXECUTING setFieldsValue ===');
        form.setFieldsValue(initialValues);
        
        // Kiểm tra form values sau khi set
        setTimeout(() => {
          const currentFormValues = form.getFieldsValue();
          console.log('=== FORM VALUES AFTER SET ===');
          console.log('currentFormValues:', currentFormValues);
          
          // Force re-render nếu cần
          if (items.length !== (initialValues.items?.length || 0)) {
            console.log('=== FORCING RE-RENDER ===');
            setForceUpdate(prev => prev + 1);
          }
        }, 100);
      }, 300);
    }
  }, [initialValues, isFormInitialized, visible, form, items.length]);

  // Lưu snapshot dữ liệu ban đầu khi mở modal
  useEffect(() => {
    if (visible && isFormInitialized) {
      setInitialFormValues(form.getFieldsValue());
      setInitialItems([...items]);
    }
  }, [visible, isFormInitialized]);

  // Cảnh báo khi đóng trình duyệt nếu có dữ liệu chưa lưu
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty()) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [form, items, initialFormValues, initialItems]);

  // Hàm kiểm tra dữ liệu đã thay đổi chưa
  const isDirty = () => {
    if (!initialFormValues) return false;
    const currentForm = form.getFieldsValue();
    return (
      JSON.stringify(currentForm) !== JSON.stringify(initialFormValues) ||
      JSON.stringify(items) !== JSON.stringify(initialItems)
    );
  };

  // Đổi tên handleCancel thành handleCancelInternal
  const handleCancelInternal = () => {
    form.resetFields();
    itemForm.resetFields();
    setInitialValues(null);
    setItems([]);
    setForceUpdate(0);
    setIsFormInitialized(false);
    onCancel();
  };

  // Thay thế handleCancel() bằng handleCancelInternal() trong handleCancelWithConfirm
  const handleCancelWithConfirm = () => {
    if (isDirty()) {
      Modal.confirm({
        title: 'Bạn có chắc muốn thoát?',
        content: 'Dữ liệu chưa lưu sẽ bị mất. Bạn có muốn tiếp tục?',
        okText: 'Thoát',
        cancelText: 'Ở lại',
        onOk: () => {
          handleCancelInternal();
        },
      });
    } else {
      handleCancelInternal();
    }
  };

  const handleAddItem = () => {
    itemForm.validateFields().then((values) => {
      const medicine = medicines.find(m => m.id === values.medicine_id);
      
      if (!medicine) return;

      const newItem: StockInItem = {
        medicine_id: values.medicine_id,
        quantity: values.quantity,
        unit_price: values.unit_price,
        lot_number: values.lot_number,
        expiry_date: values.expiry_date,
        manufacturing_date: values.manufacturing_date,
        notes: values.notes || ''
      };

      setItems([...items, newItem]);
      itemForm.resetFields();
    });
  };

  const handleRemoveItem = (index: number) => {
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
  };

  const handleSubmit = async (values: any) => {
    try {
      // Validate items
      if (items.length === 0) {
        message.error('Vui lòng thêm ít nhất một loại thuốc');
        return;
      }

      // Validate dates for each item
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.manufacturing_date && item.expiry_date) {
          if (item.manufacturing_date.isAfter(item.expiry_date, 'day')) {
            message.error(`Thuốc ${i + 1}: Ngày sản xuất không được sau hạn sử dụng`);
            return;
          }
        }
      }

      const data = {
        supplier_id: values.supplier_id,
        notes: values.notes || '',
        items: items.map((item: StockInItem) => ({
          medicine_id: item.medicine_id,
          quantity: item.quantity,
          unit_price: item.unit_price,
          lot_number: item.lot_number,
          expiry_date: item.expiry_date.format('YYYY-MM-DD'),
          manufacturing_date: item.manufacturing_date.format('YYYY-MM-DD'),
          notes: item.notes || ''
        }))
      };

      if (editingRecord) {
        // Lấy ID từ voucher nếu có cấu trúc { voucher: {...}, items: [...] }
        const id = editingRecord.voucher?.id || editingRecord.id;
        await updateMutation.mutateAsync({ id, data });
      } else {
        await createMutation.mutateAsync(data);
      }
    } catch (error) {
      // Error handling is done in mutation
    }
  };

  const columns = [
    {
      title: 'STT',
      key: 'index',
      width: 60,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: 'Tên thuốc',
      dataIndex: 'medicine_name',
      key: 'medicine_name',
      render: (_: any, record: StockInItem) => {
        const medicine = medicines.find(m => m.id === record.medicine_id);
        return (
          <div>
            <div style={{ fontWeight: 500 }}>{medicine?.name || 'N/A'}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>Mã: {medicine?.code || 'N/A'}</div>
          </div>
        );
      },
    },
    {
      title: 'Số lượng',
      key: 'quantity',
      width: 100,
      render: (_: any, record: StockInItem) => {
        const medicine = medicines.find(m => m.id === record.medicine_id);
        return (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 500 }}>{record.quantity}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>{medicine?.unit || 'N/A'}</div>
          </div>
        );
      },
    },
    {
      title: 'Đơn giá',
      key: 'unit_price',
      width: 120,
      render: (_: any, record: StockInItem) => (
        <div style={{ textAlign: 'right' }}>
          {record.unit_price.toLocaleString('vi-VN')} VNĐ
        </div>
      ),
    },
    {
      title: 'Thành tiền',
      key: 'total_amount',
      width: 130,
      render: (_: any, record: StockInItem) => (
        <div style={{ textAlign: 'right', fontWeight: 500 }}>
          {(record.quantity * record.unit_price).toLocaleString('vi-VN')} VNĐ
        </div>
      ),
    },
    {
      title: 'Số lô',
      key: 'lot_number',
      width: 120,
      render: (_: any, record: StockInItem) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.lot_number}</div>
          <div style={{ fontSize: '10px', color: '#666' }}>
            HSD: {record.expiry_date ? dayjs(record.expiry_date).format('DD/MM/YYYY') : 'N/A'}
          </div>
        </div>
      ),
    },
    {
      title: 'Ghi chú',
      key: 'notes',
      width: 120,
      render: (_: any, record: StockInItem) => (
        <div style={{ textAlign: 'center' }}>
          {record.notes || ''}
        </div>
      ),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 80,
      render: (_: any, __: any, index: number) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveItem(index)}
          size="small"
        />
      ),
    },
  ];

  const totalAmount = items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);

  return (
    <Drawer
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ 
            width: 40, 
            height: 40, 
            borderRadius: '50%', 
            background: editingRecord ? '#1890ff' : '#52c41a',
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            color: 'white',
            fontSize: '18px'
          }}>
            {editingRecord ? <EditOutlined /> : <ImportOutlined />}
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <Title level={4} style={{ margin: 0, color: '#262626' }}>
                {editingRecord ? 'Sửa phiếu nhập kho' : 'Tạo phiếu nhập kho'}
              </Title>
              <Tooltip
                title={
                  <div style={{ maxWidth: '320px' }}>
                    <div style={{ 
                      fontWeight: 'bold', 
                      marginBottom: '12px', 
                      fontSize: '14px',
                      color: '#fff',
                      borderBottom: '1px solid rgba(255,255,255,0.2)',
                      paddingBottom: '8px'
                    }}>
                      📋 Hướng dẫn tạo phiếu nhập kho
                    </div>
                    <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                      <div style={{ marginBottom: '6px' }}>
                        <span style={{ color: '#ffd591', fontWeight: 'bold' }}>🏢 Nhà cung cấp:</span> Chọn từ danh sách
                      </div>
                      <div style={{ marginBottom: '6px' }}>
                        <span style={{ color: '#ffd591', fontWeight: 'bold' }}>💊 Thuốc:</span> Chọn loại thuốc cần nhập
                      </div>
                      <div style={{ marginBottom: '6px' }}>
                        <span style={{ color: '#ffd591', fontWeight: 'bold' }}>📦 Số lượng:</span> Phải lớn hơn 0
                      </div>
                      <div style={{ marginBottom: '6px' }}>
                        <span style={{ color: '#ffd591', fontWeight: 'bold' }}>💰 Đơn giá:</span> Giá mua vào (VNĐ)
                      </div>
                      <div style={{ marginBottom: '6px' }}>
                        <span style={{ color: '#ffd591', fontWeight: 'bold' }}>🏷️ Số lô:</span> Mã lô sản xuất
                      </div>
                      <div style={{ marginBottom: '6px' }}>
                        <span style={{ color: '#ffd591', fontWeight: 'bold' }}>📅 Hạn sử dụng:</span> Ít nhất 30 ngày
                      </div>
                      <div style={{ marginBottom: '6px' }}>
                        <span style={{ color: '#ffd591', fontWeight: 'bold' }}>🏭 Ngày sản xuất:</span> Không được trong tương lai
                      </div>
                      <div style={{ marginBottom: '6px' }}>
                        <span style={{ color: '#ffd591', fontWeight: 'bold' }}>📝 Ghi chú:</span> Thông tin bổ sung (tùy chọn)
                      </div>
                    </div>
                  </div>
                }
                placement="right"
                color="#1890ff"
                trigger="hover"
                overlayStyle={{ 
                  maxWidth: '380px',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }}
                mouseEnterDelay={0.5}
              >
                <div
                  style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    background: 'rgba(24, 144, 255, 0.1)',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    border: '1px solid rgba(24, 144, 255, 0.2)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(24, 144, 255, 0.2)';
                    e.currentTarget.style.transform = 'scale(1.1)';
                    e.currentTarget.style.borderColor = '#1890ff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(24, 144, 255, 0.1)';
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.borderColor = 'rgba(24, 144, 255, 0.2)';
                  }}
                >
                  <QuestionCircleOutlined 
                    style={{ 
                      color: '#1890ff', 
                      fontSize: '14px',
                      transition: 'all 0.3s ease'
                    }}
                  />
                </div>
              </Tooltip>
            </div>
            <Text type="secondary" style={{ fontSize: '14px' }}>
              {editingRecord ? 'Cập nhật thông tin phiếu nhập kho' : 'Thêm thuốc mới vào kho'}
            </Text>
          </div>
        </div>
      }
      open={visible}
      onClose={handleCancelWithConfirm}
      width="87%"
      size="large"
      placement="right"
      style={{ 
        zIndex: 1000
      }}
      bodyStyle={{ 
        padding: '24px',
        height: 'calc(100vh - 110px)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}
      destroyOnClose
      maskStyle={{ zIndex: 999 }}
    >
      {isLoadingDetail && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          padding: '40px',
          background: 'rgba(255,255,255,0.8)',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1000
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ marginBottom: 16 }}>
              <div style={{ 
                width: 40, 
                height: 40, 
                borderRadius: '50%', 
                background: '#1890ff',
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                color: 'white',
                margin: '0 auto 16px'
              }}>
                <ImportOutlined />
              </div>
            </div>
            <div style={{ fontSize: '16px', fontWeight: 500, color: '#262626', marginBottom: 8 }}>
              Đang tải dữ liệu phiếu nhập kho...
            </div>
            <div style={{ fontSize: '14px', color: '#666' }}>
              Vui lòng chờ trong giây lát
            </div>
          </div>
        </div>
      )}
      
      <Form
        key={`${forceUpdate}-${isFormInitialized}`}
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        validateTrigger={['onChange', 'onBlur']}
        scrollToFirstError
        initialValues={initialValues}
        style={{ 
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Two Column Layout */}
        <div style={{ 
          flex: 1, 
          display: 'flex', 
          gap: '24px',
          minHeight: 0,
          overflow: 'hidden'
        }}>
          {/* Left Column - Medicine Details */}
          <div style={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column',
            minWidth: 0
          }}>
            <Title level={4} style={{ marginBottom: '16px' }}>Chi tiết thuốc nhập kho</Title>
            
            {/* Add Item Form */}
            <div style={{ 
              flexShrink: 0,
              padding: '16px', 
              border: '1px solid #d9d9d9', 
              borderRadius: '6px',
              marginBottom: '16px',
              backgroundColor: '#fafafa'
            }}>
              <Title level={5}>Thêm thuốc</Title>
              <Form form={itemForm} layout="inline">
                <Form.Item
                  name="medicine_id"
                  rules={[{ required: true, message: 'Chọn thuốc' }]}
                >
                  <Select
                    placeholder="Chọn thuốc"
                    style={{ width: 200 }}
                    showSearch
                    filterOption={(input, option: any) => {
                      const children = option?.children;
                      if (typeof children === 'string') {
                        return children.toLowerCase().includes(input.toLowerCase());
                      }
                      return false;
                    }}
                  >
                    {medicines.map(medicine => (
                      <Option key={medicine.id} value={medicine.id}>
                        {medicine.name} ({medicine.code})
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item 
                  name="quantity" 
                  rules={[{ required: true, message: 'Nhập số lượng' }]}
                >
                  <InputNumber
                    placeholder="Số lượng"
                    min={1}
                    style={{ width: 120 }}
                  />
                </Form.Item>

                <Form.Item 
                  name="unit_price" 
                  rules={[{ required: true, message: 'Nhập đơn giá' }]}
                >
                  <InputNumber
                    placeholder="Đơn giá"
                    min={0}
                    formatter={(value: number | string | undefined) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={(value: string | undefined) => value ? value.replace(/\$\s?|(,*)/g, '') : ''}
                    style={{ width: 150 }}
                  />
                </Form.Item>

                <Form.Item
                  name="lot_number"
                  rules={[{ required: true, message: 'Nhập số lô' }]}
                >
                  <Input
                    placeholder="Số lô"
                    style={{ width: 150 }}
                  />
                </Form.Item>

                <Form.Item
                  name="expiry_date"
                  rules={[{ required: true, message: 'Chọn hạn sử dụng' }]}
                >
                  <DatePicker
                    placeholder="Hạn sử dụng"
                    format="DD/MM/YYYY"
                    style={{ width: 150 }}
                  />
                </Form.Item>

                <Form.Item
                  name="manufacturing_date"
                  rules={[{ required: true, message: 'Chọn ngày sản xuất' }]}
                >
                  <DatePicker
                    placeholder="Ngày sản xuất"
                    format="DD/MM/YYYY"
                    style={{ width: 150 }}
                  />
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleAddItem}
                  >
                    Thêm
                  </Button>
                </Form.Item>
              </Form>
            </div>

            {/* Items Table */}
            <div style={{ 
              flex: 1, 
              overflow: 'auto', 
              minHeight: 0,
              border: '1px solid #d9d9d9',
              borderRadius: '6px'
            }}>
                              <Table
                  columns={columns}
                  dataSource={items}
                  rowKey={(_record, index) => index?.toString() || '0'}
                  pagination={false}
                  size="small"
                  bordered
                  scroll={{ x: 800, y: 'calc(100vh - 500px)' }}
                />
            </div>
          </div>

          {/* Right Column - Other Information */}
          <div style={{ 
            width: '400px', 
            display: 'flex', 
            flexDirection: 'column',
            flexShrink: 0
          }}>
            <Title level={4} style={{ marginBottom: '16px' }}>Thông tin nhà cung cấp</Title>
            
            {/* Supplier Information */}
            <div style={{ 
              padding: '16px', 
              border: '1px solid #d9d9d9', 
              borderRadius: '6px',
              marginBottom: '16px',
              backgroundColor: '#fafafa'
            }}>
              <Form.Item
                name="supplier_id"
                label="Nhà cung cấp"
                rules={[{ required: true, message: 'Vui lòng chọn nhà cung cấp' }]}
              >
                <Select 
                  placeholder="Chọn nhà cung cấp" 
                  showSearch
                  optionLabelProp="label"
                  filterOption={(input, option) => {
                    const label = option?.label;
                    if (typeof label === 'string') {
                      return label.toLowerCase().includes(input.toLowerCase());
                    }
                    return false;
                  }}
                  style={{ width: '100%' }}
                >
                  {suppliers.map((supplier: any) => (
                    <Option 
                      key={supplier.id} 
                      value={supplier.id}
                      label={supplier.name}
                    >
                      <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        gap: '4px',
                        padding: '4px 0'
                      }}>
                        <div style={{ 
                          fontWeight: '500', 
                          fontSize: '14px',
                          color: '#262626'
                        }}>
                          {supplier.name}
                        </div>
                        <div style={{ 
                          fontSize: '12px', 
                          color: '#666'
                        }}>
                          {supplier.contact_person} • {supplier.phone}
                        </div>
                      </div>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </div>

            {/* Summary */}
            <div style={{ 
              flexShrink: 0,
              padding: '16px',
              border: '1px solid #d9d9d9',
              borderRadius: '6px',
              marginBottom: '16px',
              backgroundColor: '#f0f8ff'
            }}>
              <Title level={5} style={{ marginBottom: '12px' }}>Tổng kết</Title>
              <div style={{ marginBottom: '8px' }}>
                <Text>Số lượng thuốc: </Text>
                <Text strong>{items.length}</Text>
              </div>
              <div style={{ marginBottom: '8px' }}>
                <Text>Tổng số lượng: </Text>
                <Text strong>{items.reduce((sum, item) => sum + item.quantity, 0)}</Text>
              </div>
              <Divider style={{ margin: '12px 0' }} />
              <div style={{ textAlign: 'right' }}>
                <Text strong style={{ fontSize: '18px', color: '#52c41a' }}>
                  Tổng tiền: {totalAmount.toLocaleString('vi-VN')} VNĐ
                </Text>
              </div>
            </div>

            {/* Notes */}
            <div style={{ 
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0
            }}>
              <Form.Item name="notes" label="Ghi chú">
                <TextArea 
                  rows={8} 
                  placeholder="Nhập ghi chú (nếu có)"
                  style={{ resize: 'none' }}
                />
              </Form.Item>
            </div>

            {/* Action Buttons */}
            <div style={{ 
              flexShrink: 0,
              textAlign: 'right', 
              marginTop: '16px',
              padding: '16px',
              borderTop: '1px solid #f0f0f0',
              backgroundColor: '#fafafa'
            }}>
              <Space>
                <Button onClick={handleCancelWithConfirm}>
                  Hủy
                </Button>
                <Button 
                  type="primary" 
                  icon={<SaveOutlined />} 
                  htmlType="submit"
                  loading={createMutation.isLoading || updateMutation.isLoading}
                >
                  {editingRecord ? 'Cập nhật' : 'Lưu phiếu'}
                </Button>
              </Space>
            </div>
          </div>
        </div>
      </Form>
    </Drawer>
  );
};

export default StockInModal; 